import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  LoadingState,
  Page,
  ResponsiveGrid,
  Select,
  Table,
  Text,
  type Column,
} from "@marekl/adminer/components";
import { pluralizeCzech } from "@marekl/adminer/format";
import { useConfirm } from "@marekl/adminer/hooks";
import { Plus, Wand2 } from "lucide-react";
import { AppTopbar } from "../../../shell/AppTopbar";
import {
  competitionCrumbs,
  lockedNotice,
  useCompetitionReadOnly,
} from "../../../shell/CompetitionLayout";
import { isFrozen } from "../../../lib/competitionState";
import { useCompetition } from "../../../lib/queries/competitions";
import { useCompetitionCategories } from "../../../lib/queries/startlists";
import {
  seatHolder,
  seatLabel,
  useAssignBlock,
  useDeletePanel,
  usePanels,
  usePreparePanels,
  type Panel,
  type PanelBlock,
  type PanelSeat,
} from "../../../lib/queries/panels";
import { PanelNameModal } from "./PanelNameModal";
import { SeatModal } from "./SeatModal";
import type { ID } from "../../../lib/id";

// „Poroty": who sits on which panel, and which blocks of the running
// order each panel judges (docs/zivy-provoz-soutezi.md §11c A.4): one
// table row per discipline block, in running order, with its panel. Laid
// out before the competition; while it runs the same edits go out as live
// commands, so the head judge's changes on site and the organizer's here
// meet in one log.
const blockWord = (n: number) => pluralizeCzech(n, ["blok", "bloky", "bloků"]);

export function PanelsPage() {
  const { id } = useParams();
  const compId = id ?? "";
  const {
    data: competition,
    isLoading,
    error,
    refetch,
  } = useCompetition(compId);
  const layout = usePanels(compId);
  const { data: categories } = useCompetitionCategories(compId);
  const running = competition?.status === "running";
  // A competitions_viewer reads the porota the way a Dokončeno
  // competition shows it: everything there, nothing to change.
  const readOnly = useCompetitionReadOnly();
  const frozen = (competition ? isFrozen(competition) : false) || readOnly;
  const editable = !frozen;

  const prepare = usePreparePanels(compId);
  const assignBlock = useAssignBlock(compId, running);
  const deletePanel = useDeletePanel(compId);
  const confirm = useConfirm();
  const [seatEdit, setSeatEdit] = useState<{
    panel: ID | null;
    seat: PanelSeat;
  } | null>(null);
  const [nameEdit, setNameEdit] = useState<Panel | "new" | null>(null);

  const panels = layout.data?.panels ?? [];
  const blocks = useMemo(() => layout.data?.blocks ?? [], [layout.data]);
  const categoryName = useMemo(() => {
    const out = new Map<ID, string>();
    for (const c of categories?.rows ?? []) out.set(c.id, c.name);
    return out;
  }, [categories]);
  const manyGroups = blocks.some((b) => (b.lane_group ?? 0) > 1);

  // Running order, never sortable: the row order is the order the panels
  // judge in.
  const blockColumns: Column<PanelBlock>[] = [
    {
      key: "block",
      header: "Blok",
      render: (b) =>
        (b.competition_category_id &&
          categoryName.get(b.competition_category_id)) ||
        b.discipline,
    },
    {
      key: "lane",
      header: "Dráha",
      render: (b) =>
        b.lane
          ? manyGroups
            ? `Skupina drah ${b.lane_group} · Dráha ${b.lane}`
            : `Dráha ${b.lane}`
          : "",
    },
    {
      key: "needs",
      header: "Porotci",
      render: (b) => (
        <Text variant="muted">
          {b.plus_judges} + {b.tech_judges}
        </Text>
      ),
    },
    {
      key: "panel",
      header: "Porota",
      render: (b) => (
        <Select
          aria-label="Porota bloku"
          value={b.panel_id ?? ""}
          disabled={!editable || assignBlock.isPending}
          onChange={(e) => {
            if (e.target.value)
              assignBlock.mutate({ item: b.item_id, panel: e.target.value });
          }}
        >
          {!b.panel_id && (
            <option value="" disabled>
              Bez poroty
            </option>
          )}
          {panels.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </Select>
      ),
    },
  ];
  const unassigned = blocks.filter((b) => !b.panel_id).length;

  const seatColumns = (panelId: ID | null): Column<PanelSeat>[] => [
    {
      key: "seat",
      header: "Místo",
      render: (s) => (
        <>
          {seatLabel(s)}
          {s.extra && (
            <>
              {" "}
              <Badge variant="muted">navíc</Badge>
            </>
          )}
        </>
      ),
    },
    {
      key: "judge",
      header: "Porotce",
      render: (s) => {
        const who = seatHolder(s);
        if (!who) return <Text variant="muted">Neobsazeno</Text>;
        return (
          <>
            {who}
            {s.judge_name && (
              <>
                {" "}
                <Text variant="muted">(mimo federaci)</Text>
              </>
            )}
          </>
        );
      },
    },
    {
      key: "actions",
      header: "",
      render: (s) =>
        editable && (
          <Table.Actions>
            <Table.Action
              onClick={() => setSeatEdit({ panel: panelId, seat: s })}
            >
              {seatHolder(s) ? "Změnit" : "Obsadit"}
            </Table.Action>
          </Table.Actions>
        ),
    },
  ];

  const crumbs = competitionCrumbs(compId, competition, { label: "Poroty" });
  if (isLoading || layout.isLoading) {
    return (
      <>
        <AppTopbar crumbs={crumbs} />
        <Page>
          <Page.Content>
            <LoadingState />
          </Page.Content>
        </Page>
      </>
    );
  }
  if (error || layout.error) {
    return (
      <>
        <AppTopbar crumbs={crumbs} />
        <Page>
          <Page.Content>
            <ErrorState
              error={error ?? layout.error}
              onRetry={() => (error ? refetch() : layout.refetch())}
            />
          </Page.Content>
        </Page>
      </>
    );
  }

  const head = layout.data?.head_judge ?? {
    kind: "head" as const,
    position: 1,
  };

  return (
    <>
      <AppTopbar crumbs={crumbs} />
      <Page>
        <Page.Header
          title="Poroty"
          subtitle="Která porota hodnotí který blok a kdo sedí na kterém místě."
          actions={
            editable && (
              <>
                {!running && unassigned > 0 && (
                  <Button
                    weight="outline"
                    startIcon={<Wand2 aria-hidden="true" />}
                    loading={prepare.isPending}
                    onClick={() => prepare.mutate()}
                  >
                    Připravit poroty
                  </Button>
                )}
                <Button
                  startIcon={<Plus aria-hidden="true" />}
                  onClick={() => setNameEdit("new")}
                >
                  Nová porota
                </Button>
              </>
            )
          }
        />
        <Page.Content stack gap="lg">
          {running && (
            <Alert variant="info">
              Soutěž probíhá: změny se zapisují do živého provozu a hned je vidí
              předseda poroty i zařízení v hale. Blok, ve kterém už byla zavolána
              přihláška, svou porotu nemění.
            </Alert>
          )}
          {frozen && <Alert variant="info">{lockedNotice(competition, readOnly)}</Alert>}
          {blocks.length === 0 ? (
            <EmptyState
              title="Startovní listina zatím nemá bloky disciplín"
              description="Poroty se přidělují blokům startovní listiny. Nejdřív ji sestavte."
            />
          ) : (
            <>
              {unassigned > 0 && !running && (
                <Alert variant="warn">
                  Bez poroty: {unassigned} {blockWord(unassigned)}. „Připravit
                  poroty“ jim dá porotu bloku nad nimi; kde žádná není, založí
                  novou, a každé dráze vlastní.
                </Alert>
              )}

              <Card>
                <Card.Header title="Předseda poroty" />
                <Card.Content padding="none">
                  <Table<PanelSeat>
                    rows={[head]}
                    rowKey={() => "head"}
                    columns={seatColumns(null)}
                  />
                </Card.Content>
              </Card>

              <ResponsiveGrid columns={2}>
                {panels.map((p) => (
                  <Card key={p.id}>
                    <Card.Header
                      title={p.name}
                      subtitle={`${p.plus_judges} plusových, ${p.tech_judges} technických · hodnotí ${p.blocks.length} ${blockWord(p.blocks.length)}`}
                      actions={
                        editable &&
                        !running && (
                          <>
                            <Button
                              size="sm"
                              weight="ghost"
                              onClick={() => setNameEdit(p)}
                            >
                              Přejmenovat
                            </Button>
                            <Button
                              size="sm"
                              weight="ghost"
                              variant="danger"
                              onClick={() =>
                                confirm({
                                  title: "Smazat porotu",
                                  description: (
                                    <>
                                      Smazat porotu <strong>{p.name}</strong>?
                                      Její bloky zůstanou bez poroty a obsazená
                                      místa se uvolní.
                                    </>
                                  ),
                                  confirmLabel: "Smazat",
                                  variant: "danger",
                                  onConfirm: async () => {
                                    await deletePanel.mutateAsync(p.id);
                                  },
                                })
                              }
                            >
                              Smazat
                            </Button>
                          </>
                        )
                      }
                    />
                    <Card.Content padding="none">
                      <Table<PanelSeat>
                        rows={p.seats ?? []}
                        rowKey={(s) => `${s.kind}-${s.position}`}
                        columns={seatColumns(p.id)}
                        empty={
                          <EmptyState
                            title="Porota zatím nic nehodnotí"
                            description="Vyberte ji u bloku v tabulce níže; místa se odvodí z jeho kombinace."
                          />
                        }
                      />
                    </Card.Content>
                  </Card>
                ))}
              </ResponsiveGrid>

              <Page.SectionHeader
                title="Bloky"
                meta={`${blocks.length} ${blockWord(blocks.length)} disciplín v pořadí startovní listiny`}
              />
              <Table<PanelBlock>
                framed
                rows={blocks}
                rowKey={(b) => b.item_id}
                columns={blockColumns}
              />
            </>
          )}
        </Page.Content>
      </Page>

      <SeatModal
        competitionId={compId}
        running={running}
        panelId={seatEdit?.panel ?? null}
        seat={seatEdit?.seat ?? null}
        onClose={() => setSeatEdit(null)}
      />
      <PanelNameModal
        competitionId={compId}
        running={running}
        target={nameEdit}
        onClose={() => setNameEdit(null)}
      />
    </>
  );
}
